import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useUsersApi } from "../../http/users/api";
import { formatSalesRoles, User, UserSalesRole } from "../../http/users/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDate, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import SvgAdd from "../icons/Add";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import Button from "../ui/Button";
import { Check } from "../ui/Check";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import BooleanColumnFilter from "../ui/columnFilters/BooleanColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import DeleteUserDialog from "./DeleteUserDialog";

const USER_FIELDS = [
  "id",
  "createdAt",
  "firstName",
  "lastName",
  "email",
  "department",
  "manager",
  "salesRole",
  "birthDate",
  "drivingLicenseExpiration",
  "currentCostCenter",
  "distanceInMiles",
  "hasImportErrors",
  "userSubsidiariesIDs[]",
  "hidden",
  "versionNumber",
  "isRothoblaasUser",
] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = NestedPick<User, UserField>;

const Users: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const usersApi = useUsersApi();
  const params = new URLSearchParams(window.location.search);
  const filters = useListFilters({
    isSearchable: true,
  });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const navigate = useNavigate();
  const [itemToDelete, setItemToDelete] = useState<UserSearch>();

  const withImportErrors = params.get("with_import_errors") === "1";
  const noCostCenter = params.get("no_cost_center") === "1";

  useEffect(() => {
    withImportErrors && filters.setFilters("user.hasImportErrors", { operator: undefined, values: [true] });
    noCostCenter && filters.setFilters("user.costCenter", { operator: undefined, values: [null] });
  }, [location.search]);

  useEffect(() => {
    const hasErrors = params.has("with_import_errors");
    const hasCostCenter = params.has("no_cost_center");

    if (!hasErrors) {
      filters.setFilters("user.hasImportErrors", { operator: undefined, values: [] });
    }
    if (!hasCostCenter) {
      filters.setFilters("user.costCenter", { operator: undefined, values: [] });
    }
  }, [location.search]);

  const query: PaginationQueryWithFields<UserSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: USER_FIELDS,
  };

  const navToEdit = (item: UserSearch) => navigate(`/admin/users/${item.id}`);

  const result = usersApi.useAdminUsers(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: usersApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const baseUrl = `/admin/users`;

  const viewItems = [
    {
      label: i18n.t.users.all,
      url: baseUrl,
    },
    {
      label: i18n.t.common.usersWithImportErrors,
      url: `${baseUrl}?with_import_errors=1`,
    },
    {
      label: i18n.t.common.userWithoutCostCenter,
      url: `${baseUrl}?no_cost_center=1`,
    },
  ];

  const title = withImportErrors
    ? i18n.t.common.usersWithImportErrors
    : noCostCenter
      ? i18n.t.common.userWithoutCostCenter
      : i18n.t.users.all;

  const items = viewItems.filter((item) => item.label !== title);

  const views = { title, items };

  const getRowActions = (item: UserSearch) => {
    const actions: Action[] = [];

    if (userSettings.permissions.users.update) {
      actions.push(getEditAction(i18n, () => navToEdit(item)));
    }

    if (userSettings.permissions.users.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Header title={withImportErrors ? i18n.t.common.usersWithImportErrors : i18n.t.users.plural} />
      <Panel>
        <Table<UserSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          views={views}
          buttons={{
            main: !!userSettings.permissions.users.insert && (
              <Button buttonProps={{ onClick: () => navigate("/admin/users/new") }} glyph={SvgAdd} />
            ),
          }}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="user.name" order={orderOptions} />
              <Table.Th title={i18n.t.common.email} fieldName="user.email" order={orderOptions} />

              <Table.Th
                title={i18n.t.common.department}
                fieldName="user.department"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.manager}
                fieldName="user.manager.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.salesRole}
                fieldName="user.salesRoles"
                filters={filterOptions}
                filter={TextColumnFilter}
                formatter={(value) => formatSalesRoles(i18n.t, value as UserSalesRole)}
              />
              <Table.Th
                title={i18n.t.common.birthdate}
                fieldName="user.birthDate"
                order={orderOptions}
                formatter={(value) => (value ? formatDate(value as string, i18n.language) : undefined)}
              />
              <Table.Th
                title={i18n.t.common.drivingLicenseExpirationDate}
                fieldName="user.drivingLicenseExpirationDate"
                order={orderOptions}
                formatter={(value) => (value ? formatDate(value as string, i18n.language) : undefined)}
              />
              <Table.Th title={i18n.t.common.costCenter} fieldName="user.costCenter" order={orderOptions} />
              <Table.Th
                title={i18n.t.common.distanceInMiles}
                fieldName="user.distanceInMiles"
                filters={filterOptions}
                filter={BooleanColumnFilter}
                order={orderOptions}
              />
              {!withImportErrors && (
                <Table.Th
                  title={i18n.t.common.hasImportErrors}
                  fieldName="user.hasImportErrors"
                  filters={filterOptions}
                  filter={BooleanColumnFilter}
                />
              )}
              <Table.Th
                title={i18n.t.common.hidden}
                fieldName="user.hidden"
                filters={filterOptions}
                filter={BooleanColumnFilter}
              />
              <Table.Th
                title={i18n.t.common.isRothoblaasUser}
                fieldName="user.isRothoblaasUser"
                filters={filterOptions}
                filter={BooleanColumnFilter}
              />
              <th></th>
            </>
          }
          onRowClick={(item) => navigate(`/admin/users/${item.id}`)}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                <CustomLink to={`/admin/users/${item.id}`}>
                  {item.firstName} {item.lastName}
                </CustomLink>
              </td>
              <td>
                <CustomLink to={`mailto:${item.email}`}>{item.email}</CustomLink>
              </td>
              <td>{item.department}</td>
              <td>
                <CustomLink to={`/admin/users/${item.manager?.id}`}>{item.manager?.fullName}</CustomLink>
              </td>
              <td>{!!item.salesRole && formatSalesRoles(i18n.t, item.salesRole)}</td>
              <td>{!!item.birthDate && formatDateNumeric(item.birthDate, i18n.language)}</td>
              <td align="center">
                {!!item.drivingLicenseExpiration && formatDateNumeric(item.drivingLicenseExpiration, i18n.language)}
              </td>
              <td>{item.currentCostCenter}</td>
              <td align="center">
                <Check
                  icon={item.distanceInMiles ? SvgCheckCircle : SvgCancel}
                  color={item.distanceInMiles ? "green" : "red"}
                  obscured={!item.distanceInMiles}
                />
              </td>
              {!withImportErrors && (
                <td align="center">
                  <Check
                    icon={item.hasImportErrors ? SvgCheckCircle : SvgCancel}
                    color={item.hasImportErrors ? "red" : "green"}
                    obscured={!item.hasImportErrors}
                  />
                </td>
              )}
              <td align="center">
                <Check
                  icon={item.hidden ? SvgCheckCircle : SvgCancel}
                  color={item.hidden ? "red" : "green"}
                  obscured={!item.hidden}
                />
              </td>
              <td align="center">
                <Check
                  icon={item.isRothoblaasUser ? SvgCheckCircle : SvgCancel}
                  color={item.isRothoblaasUser ? "green" : "red"}
                  obscured={!item.isRothoblaasUser}
                />
              </td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>
      {!!itemToDelete && <DeleteUserDialog userId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />}
    </>
  );
};

export default Users;
